// axiosWithAuth is intentional here: this endpoint returns a flat JSON response,
// not JSONAPI format, so the `api` wrapper (jsonapi-react) would mis-deserialize it.
//
// NOTE: axiosWithAuth runs mapKeysToCase on every response, so all keys arrive in
// camelCase. RawMessage and RawVersionThread use camelCase field names accordingly.
import axiosWithAuth from '@/utils/axiosWithAuth';
import {
  ConversationMessage,
  UnifiedFeedItem,
  UnifiedFeedPage,
} from '@/types/conversationMessage';
import {NegotiationSnapshot} from '@/types/negotiationSnapshot';

const CONVERSATIONS_PATH = 'conversations';
const PER_PAGE = 25;

type RawSender = {
  id: string;
  type: string;
  name: string | null;
  imageUrl: string | null;
};

type RawMessageAttachment = {
  id: string;
  fileName: string;
  mimeType: string;
  viewableUrl: string;
  downloadUrl: string;
};

type RawGigDeliverableChange = {
  kind: string;
  oldRequiredCount: number | null;
  newRequiredCount: number | null;
  campaignNetwork: string | null;
  displayText: string | null;
};

type RawNegotiationSnapshot = {
  id: string;
  costInCents: number;
  previousCostInCents: number;
  contentAgreementTimePeriodInMonths: number | null;
  previousContentAgreementTimePeriodInMonths: number | null;
  state: string;
  gigDeliverableChanges: RawGigDeliverableChange[];
  brandNegotiation?: {
    id: string;
    state: string;
    contract?: {id: string};
  };
};

type RawMessageBase = {
  id: string;
  campaignId: string | null;
  body: string;
  bodyHtml: string;
  createdAt: string;
  signerDisplayName: string | null;
  contentSubmissionVersionId: string | null;
  campaignName: string | null;
  sender: RawSender | null;
  messageAttachments: RawMessageAttachment[];
};

type RawMessage = RawMessageBase & {type: 'message'};

type RawVersionFeedbackMessage = RawMessageBase & {type: 'version_feedback'};

type RawNegotiationSnapshotMessage = RawMessageBase & {
  type: 'negotiation_snapshot';
  negotiationSnapshot: RawNegotiationSnapshot;
};

type RawVersionThread = {
  type: 'version_thread';
  versionId: string;
  contentSubmissionId: string;
  campaignId: string;
  thumbnailUrl: string | null;
  versionNumber: number;
  campaignName: string;
  contentType: string;
  status: string;
  messageCount: number;
  hasUnread: boolean;
  messages: (RawMessage | RawVersionFeedbackMessage)[];
};

type RawFeedItem =
  | RawMessage
  | RawVersionFeedbackMessage
  | RawNegotiationSnapshotMessage
  | RawVersionThread;

function senderKind(type: string): 'creator' | 'brand' | 'agency' {
  if (type === 'Merchant') return 'brand';
  if (type === 'Agency') return 'agency';
  return 'creator';
}

function mapRawNegotiationSnapshot(
  raw: RawNegotiationSnapshot
): NegotiationSnapshot {
  return {
    id: raw.id,
    costInCents: raw.costInCents,
    previousCostInCents: raw.previousCostInCents,
    contentAgreementTimePeriodInMonths: raw.contentAgreementTimePeriodInMonths,
    previousContentAgreementTimePeriodInMonths:
      raw.previousContentAgreementTimePeriodInMonths,
    state: raw.state as NegotiationSnapshot['state'],
    gigDeliverableChanges: raw.gigDeliverableChanges,
    brandNegotiation: raw.brandNegotiation
      ? {
          id: raw.brandNegotiation.id,
          state: raw.brandNegotiation.state as NegotiationSnapshot['state'],
          contract: raw.brandNegotiation.contract,
        }
      : undefined,
  };
}

function mapRawMessage(
  raw: RawMessage | RawVersionFeedbackMessage | RawNegotiationSnapshotMessage
): ConversationMessage {
  const negotiationSnapshot =
    'negotiationSnapshot' in raw && raw.negotiationSnapshot
      ? mapRawNegotiationSnapshot(raw.negotiationSnapshot)
      : undefined;
  return {
    id: raw.id,
    campaignId: raw.campaignId,
    body: raw.body,
    bodyHtml: raw.bodyHtml,
    createdAt: raw.createdAt,
    signerDisplayName: raw.signerDisplayName ?? '',
    contentSubmissionVersionId: raw.contentSubmissionVersionId ?? undefined,
    campaignName: raw.campaignName ?? undefined,
    messageAttachments: raw.messageAttachments.map((attachment) => ({
      id: attachment.id,
      fileName: attachment.fileName,
      mimeType: attachment.mimeType,
      viewableUrl: attachment.viewableUrl,
      downloadUrl: attachment.downloadUrl,
    })),
    sender: raw.sender
      ? {
          id: raw.sender.id,
          name: raw.signerDisplayName || raw.sender.name || '',
          imageUrl: raw.sender.imageUrl ?? '',
          kind: senderKind(raw.sender.type),
        }
      : undefined,
    ...(negotiationSnapshot ? {negotiationSnapshot} : {}),
    ...(raw.type === 'version_feedback' ? {isMerchantFeedback: true} : {}),
  };
}

function toFeedItem(raw: RawFeedItem): UnifiedFeedItem {
  if (raw.type === 'version_thread') {
    return {
      kind: 'version_thread',
      data: {
        versionId: raw.versionId,
        contentSubmissionId: raw.contentSubmissionId,
        campaignId: raw.campaignId,
        thumbnailUrl: raw.thumbnailUrl,
        versionNumber: raw.versionNumber,
        campaignName: raw.campaignName,
        contentType: raw.contentType,
        status: raw.status,
        messageCount: raw.messageCount,
        hasUnread: raw.hasUnread,
        messages: raw.messages.map(mapRawMessage),
      },
    };
  }
  if (raw.type === 'negotiation_snapshot') {
    return {
      kind: 'negotiation_snapshot',
      data: mapRawMessage(raw) as ConversationMessage & {
        negotiationSnapshot: NegotiationSnapshot;
      },
    };
  }
  return {kind: 'message', data: mapRawMessage(raw)};
}

const unifiedFeedFetchers = {
  list: async (
    conversationId: string,
    cursor?: string,
    campaignId?: string
  ): Promise<UnifiedFeedPage> => {
    const result = await axiosWithAuth.get<{
      data: RawFeedItem[];
      nextCursor: string | null;
      firstUnreadMessageId: string | null;
    }>(`${CONVERSATIONS_PATH}/${conversationId}/unified_feed`, {
      params: {
        ...(cursor ? {cursor} : {}),
        ...(campaignId ? {campaign_id: campaignId} : {}),
        per_page: PER_PAGE,
      },
    });

    return {
      items: result.data.data.map(toFeedItem),
      nextCursor: result.data.nextCursor,
      firstUnreadMessageId: result.data.firstUnreadMessageId,
    };
  },
};

export default unifiedFeedFetchers;
